// Installed products (device page, column 3): what else is fitted to the vehicle besides the
// tracker – fuel sensors, temperature probes, CAN readers… – for support and later billing.
// Removing marks it removed (kept for history); delete is only for entries added by mistake.
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { extras, Product, ProductInput } from '../api';
import { useAuth } from '../auth';
import { dayOnly, irishDate } from '../format';

const EMPTY: ProductInput = { kind: 'fuel_sensor', model: '', serial: '', installedOn: irishDate(), notes: '' };

export function Products({ vehicleId, onChanged }: { vehicleId: number; onChanged?: () => void }) {
  const { user } = useAuth();
  const [data, setData] = useState<{ kinds: Record<string, string>; products: Product[] } | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<ProductInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(() => {
    extras.products(vehicleId).then((d) => { setData(d); setError(''); }).catch((e) => setError((e as Error).message));
  }, [vehicleId]);
  useEffect(load, [load]);

  const run = async (job: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await job();
      load();
      onChanged?.();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };
  const add = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await extras.addProduct(vehicleId, { ...f, installedOn: f.installedOn || null });
      setAdding(false);
      setF(EMPTY);
    });
  };
  const remove = (p: Product) => {
    const day = window.prompt(`Removed on (YYYY-MM-DD) – ${data!.kinds[p.kind]}${p.model ? ` ${p.model}` : ''}:`, irishDate());
    if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) void run(() => extras.saveProduct(p.id, { removedOn: day }));
  };
  const del = (p: Product) => {
    if (window.confirm('Delete this entry? Only for entries added by mistake – use "Removed" when the product was taken out.')) void run(() => extras.deleteProduct(p.id));
  };

  const current = data?.products.filter((p) => !p.removedOn) ?? [];
  const removed = data?.products.filter((p) => p.removedOn) ?? [];
  const row = (p: Product) => (
    <li key={p.id} className={p.removedOn ? 'prod removed' : 'prod'}>
      <div>
        <b>{data!.kinds[p.kind] ?? p.kind}</b>{p.model && <> · {p.model}</>}
        {p.serial && <span className="muted"> · serial <span className="mono">{p.serial}</span></span>}
      </div>
      <div className="muted small">
        {p.installedOn ? `fitted ${dayOnly(p.installedOn)}` : 'fitting date not known'}
        {p.removedOn && <> · removed {dayOnly(p.removedOn)}</>}
        {p.createdBy && <> · added by {p.createdBy}</>}
      </div>
      {p.notes && <div className="small prewrap">{p.notes}</div>}
      <div className="actions">
        {!p.removedOn && <button type="button" className="link small" onClick={() => remove(p)} disabled={busy}>Removed…</button>}
        {(user?.role === 'admin' || p.createdBy === user?.name) && <button type="button" className="link danger small" onClick={() => del(p)} disabled={busy}>Delete</button>}
      </div>
    </li>
  );

  return (
    <section className="card form" aria-label="Installed products">
      <div className="page-head">
        <h2>Installed products</h2>
        {!adding && <button type="button" className="link" onClick={() => setAdding(true)}>Add</button>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {adding && data && (
        <form className="form subpanel" onSubmit={add}>
          <div className="grid2">
            <label>Product
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                {Object.entries(data.kinds).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label>Model<input type="text" maxLength={80} value={f.model ?? ''} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="e.g. Escort TD-BLE" /></label>
            <label>Serial number<input type="text" maxLength={80} value={f.serial ?? ''} onChange={(e) => setF({ ...f, serial: e.target.value })} /></label>
            <label>Fitted on<input type="date" value={f.installedOn ?? ''} max={irishDate()} onChange={(e) => setF({ ...f, installedOn: e.target.value })} /></label>
          </div>
          <label>Notes<textarea rows={2} maxLength={1000} value={f.notes ?? ''} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. probe in the front compartment, wired to DIN2" /></label>
          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>Add product</button>
            <button type="button" className="link" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && current.length === 0 && !adding && <p className="muted small">Nothing besides the tracker recorded yet.</p>}
      {current.length > 0 && <ul className="prods">{current.map(row)}</ul>}
      {removed.length > 0 && (
        <details open={showRemoved} onToggle={(e) => setShowRemoved((e.target as HTMLDetailsElement).open)}>
          <summary>Removed earlier ({removed.length})</summary>
          <ul className="prods">{removed.map(row)}</ul>
        </details>
      )}
    </section>
  );
}
